import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Avatar, IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { AddPhotoAlternateSharp, Close } from '@mui/icons-material';
import heic2any from 'heic2any';
import { produce } from 'immer';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useRecoilState } from 'recoil';

import { H4 } from '../../components/Heading';
import { IMAGE_TYPES } from '../../constants/images';
import { addressRepo } from '../../repositories';
import { verifiedPhotoState } from '../../states/address';
import { selectedShipmentInfoState } from '../../states/shipment';
import { convertImage } from '../../utils/image';
import './style.css';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '600px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

const modalPreviewImage = {
  p: 0,
  width: '800px',
  bgcolor: 'transparent',
};

function Verify(props) {
  const { address } = props;
  const [info, setInfo] = useRecoilState(selectedShipmentInfoState);
  const [verifiedPhotos, setVerifiedPhotos] = useRecoilState(verifiedPhotoState);
  const [imageUpload, setImageUpload] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const [openPreview, setOpenPreview] = useState(false);
  const handleClosePreview = () => setOpenPreview(false);
  const [previewUrl, setPreviewUrl] = useState();
  const [openRemoveImage, setOpenRemoveImage] = React.useState(false);
  const [isConverting, setIsConverting] = React.useState(false);
  const [selectedPhoto, setSelectedPhoto] = React.useState(null);
  const handleClickOpenRemoveImage = (obj) => {
    setOpenRemoveImage(true);
    setSelectedPhoto(obj);
  };
  const handleCloseOpenRemoveImage = () => {
    setOpenRemoveImage(false);
    setSelectedPhoto(null);
  };

  const ref = useRef();

  const imageUploadPreviewUrl = useMemo(() => {
    if (imageUpload) {
      return URL.createObjectURL(imageUpload);
    }
    return null;
  }, [imageUpload]);

  useEffect(() => {
    return () => {
      if (imageUploadPreviewUrl) {
        URL.revokeObjectURL(imageUploadPreviewUrl);
      }
    };
  }, [imageUploadPreviewUrl]);

  useEffect(() => {
    setVerifiedPhotos(address?.photos_verified ? address.photos_verified : []);
  }, [address]);

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => {
    setImageUpload(null);
    setUploadSuccess(false);
    setOpen(true);
  };
  const handleClose = () => {
    if (isUploading) return;
    setOpen(false);
  };

  const onChangeImage = async (e) => {
    setUploadSuccess(false);
    setIsUploading(false);

    const image = e.target.files?.[0];
    if (!image) return;

    const type = image.type;
    if (!IMAGE_TYPES.includes(type)) {
      toast.error('Image type is not supported');
      return;
    }

    setIsConverting(true);
    try {
      if (!['image/heic', 'image/heif'].includes(type)) {
        const result = await convertImage(image, 'jpeg');
        setImageUpload(result);
        setIsConverting(false);
        return;
      }

      let blobURL = URL.createObjectURL(image);
      try {
        let blobRes = await fetch(blobURL);
        let blob = await blobRes.blob();
        let result = await heic2any({
          blob,
          toType: 'image/jpeg',
          quality: 0.8,
        });

        setImageUpload(result);
        setIsConverting(false);
      } finally {
        URL.revokeObjectURL(blobURL);
      }
    } catch (error) {
      toast.error('Failed to convert image. Please try a different image.');
      setIsConverting(false);
      setImageUpload(null);
      if (ref.current) {
        ref.current.value = null;
      }
    }
  };

  const uploadImage = () => {
    setIsUploading(true);
    setUploadSuccess(false);

    addressRepo.addVerifyPhoto(address.id, info.shipment.id, imageUpload, (image) => {
      if (ref.current) {
        ref.current.value = null;
      }
      setIsUploading(false);
      setUploadSuccess(true);
      setImageUpload(null);
      setVerifiedPhotos([...verifiedPhotos, image]);
    }).catch(() => {
      toast.error('Failed to upload image. Please try again.');
      if (ref.current) {
        ref.current.value = null;
      }
      setIsUploading(false);
      setUploadSuccess(false);
      setImageUpload(null);
    });
  };

  const removeImage = () => {
    addressRepo
      .removeVerifyPhoto(address.id, selectedPhoto.id, info.shipment.id)
      .then(() => {
        const removedPhoto = verifiedPhotos.filter((p) => p.id === selectedPhoto.id).pop();
        if (removedPhoto.is_pod || !!removedPhoto.pod_id) {
          setInfo(
            produce(info, (draft) => {
              draft.pods = draft.pods.map((p) => {
                if (p.id === removedPhoto.pod_id) {
                  p.status = 'CREATED';
                }
                return p;
              });
            }),
          );
        }
        setVerifiedPhotos(verifiedPhotos.filter((photo) => photo.id !== selectedPhoto.id));
        setOpenRemoveImage(false);
        setSelectedPhoto(null);
      })
      .catch(() => {
        toast.error('Failed to remove photo. Please try again.');
        setOpenRemoveImage(false);
      });
  };

  const previewImage = (url) => {
    setOpenPreview(true);
    setPreviewUrl(url);
  };

  return (
    <div style={{ marginTop: '20px' }}>
      <H4>
        Verified photos
        <AddPhotoAlternateSharp className="add-icon" onClick={handleOpen}></AddPhotoAlternateSharp>
      </H4>
      {verifiedPhotos.length === 0 && <Typography style={{ fontSize: '0.8em', color: 'rgb(136, 136, 136)', marginTop: '5px' }}>No photo</Typography>}
      <div className="list-items">
        {verifiedPhotos &&
          verifiedPhotos.map((photo) => {
            return (
              <div key={photo.id}>
                <img src={photo.signed_url} onClick={() => previewImage(photo.signed_url)} />
                <Close onClick={() => handleClickOpenRemoveImage(photo)}></Close>
                {'uploaded_ts' in photo && 'uploaded_by_name' in photo && (
                  <p style={{ color: 'rgb(136, 136, 136)', fontSize: '0.8em' }}>
                    uploaded at {moment(photo.uploaded_ts).format('MM/DD hh:mmA')} by {photo.uploaded_by_name}
                  </p>
                )}
              </div>
            );
          })}
      </div>

      <Modal open={openPreview} onClose={handleClosePreview}>
        <Box sx={{ ...style, ...modalPreviewImage }}>
          <Box style={{ maxHeight: 600, textAlign: 'center', overflowY: 'scroll' }}>
            <img src={previewUrl} style={{ width: '100%' }} />
          </Box>
        </Box>
      </Modal>

      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <IconButton onClick={handleClose} className="dialog-close-btn">
            <Close />
          </IconButton>

          <Typography variant="h6" component="h2" style={{ marginBottom: '20px' }}>
            Upload Image
          </Typography>
          {imageUploadPreviewUrl && !isConverting && (
            <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
              <Avatar variant="square" src={imageUploadPreviewUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </Box>
          )}
          <input accept={IMAGE_TYPES.join(', ')} id="raised-button-file" multiple type="file" onChange={onChangeImage} ref={ref} disabled={isConverting} />
          <div style={{ marginTop: '20px' }}>
            <Button variant="contained" color="primary" disabled={isUploading === true || imageUpload === null || isConverting} onClick={uploadImage}>
              {isConverting ? 'Converting...' : 'Upload'}
              {isUploading && <span> ... </span>}
            </Button>
          </div>
          {uploadSuccess && <Typography style={{ color: '#2e7d32', marginTop: '20px' }}>Image uploaded successfully</Typography>}
        </Box>
      </Modal>

      <Dialog open={openRemoveImage} onClose={handleCloseOpenRemoveImage} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">Do you want to remove this photo?</DialogTitle>
        <DialogActions>
          <Button onClick={handleCloseOpenRemoveImage} style={{ fontWeight: 'bold' }} color="primary">
            Disagree
          </Button>
          <Button onClick={removeImage} style={{ fontWeight: 'bold' }} color="primary">
            Agree
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Verify;
